import {onUnmounted, ref} from "vue";
import {TaskResult} from "@/core/interfaces/task-result";

interface StartTaskParam<T> {
    runTask: () => Promise<TaskResult<T>>;
    pollTask: (taskId: number) => Promise<TaskResult<T>>;
}

/**
 * Task result composable.
 *
 * It first create a task by calling the `runTask` function, then it polls the task status
 * When task is completed it sets the result.
 */
export function useTaskResult<T>() {
    const loading = ref(false);
    const task = ref<TaskResult<T> | null>(null);
    const result = ref<T | null>(null);
    const error = ref<string | null>(null);

    let pollInterval: NodeJS.Timeout | null = null;

    /**
     * Start a task and poll its status until completion.
     */
    const startTask = async (params: StartTaskParam<T>) => {
        loading.value = true;
        result.value = null;
        error.value = null;
        task.value = null;

        try {
            const data = await params.runTask();
            task.value = data;

            return await pollTaskStatus(data, params.pollTask);
        } catch {
            error.value = 'An error occurred while starting the task.';
            loading.value = false;
            return;
        }
    };

    /**
     * Poll the task status until completion.
     */
    const pollTaskStatus = async (
        _task: TaskResult<T>,
        pollTask: (taskId: number) => Promise<TaskResult<T>>
    ) => {
        return new Promise((resolve, reject) => {
            pollInterval = setInterval(async () => {
                try {
                    const data = await pollTask(_task.id);
                    task.value = data;

                    if (data.status === 'completed') {
                        result.value = data.result || null;
                        loading.value = false;
                        stopPolling();
                        resolve(data.result);
                    }
                } catch (e) {
                    error.value = 'An error occurred while polling the task status.';
                    loading.value = false;
                    stopPolling();
                    reject(e);
                }
            }, 4000);
        });
    };

    /**
     * Stop polling the task status.
     */
    const stopPolling = () => {
        if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
        }
    };

    // Clean up on unmount
    onUnmounted(() => {
        stopPolling();
    });

    return {
        loading,
        task,
        result,
        error,
        startTask,
    };
}

export default useTaskResult;
